import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  LinearProgress,
  IconButton,
  Popover,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { WidgetContainer } from "../../shared/widgetContainer/widgetContainer";
import styles from "./jiraProductivity.module.css";
import { useTheme } from "@mui/material/styles";

const JiraProductivity = ({
  data = { weekly: [], monthly: [], yearly: [] },
  excludedUsers = [],
  title = "",
  loading = true,
  userClicked = () => {},
}) => {
  const [view, setView] = useState("weekly");
  const [hiddenUsers, setHiddenUsers] = useState(new Set(excludedUsers));
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = useTheme();
  const handleToggle = (_, newView) => {
    if (newView) setView(newView);
  };

  const toggleUserVisibility = (assignee) => {
    setHiddenUsers((prev) => {
      const updated = new Set(prev);
      if (updated.has(assignee)) {
        updated.delete(assignee);
      } else {
        updated.add(assignee);
      }
      return updated;
    });
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const productivityData = useMemo(() => {
    return (data[view] || []).filter((item) => !hiddenUsers.has(item.assignee));
  }, [data, view, hiddenUsers]);

  const allUsers = useMemo(() => {
    const seen = new Set();
    return (data[view] || [])
      .filter((d) => {
        if (seen.has(d.assignee)) return false;
        seen.add(d.assignee);
        return true;
      })
      .map((d) => d.assignee);
  }, [data, view]);

  return (
    <WidgetContainer
      title={`${title} ${
        view === "weekly"
          ? "(Last 7 Days)"
          : view === "monthly"
            ? "(Last 30 Days)"
            : "(12 Month Trend)"
      }`}
      description="View workload across Cloud Operations teams"
      loaded={!loading}
      maximize={true}
    >
      {Array.isArray(productivityData) && productivityData.length > 0 && (
        <Box sx={{ position: "relative", mb: 1 }}>
          {/* Filter Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* View Toggle */}

            <Box sx={{ display: "flex", justifyContent: "center", mb: 0 }}>
              <ToggleButtonGroup
                size="small"
                value={view}
                exclusive
                onChange={handleToggle}
                sx={{
                  backgroundColor: theme.palette.background.accent,
                  borderRadius: "8px",
                }}
              >
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="yearly">Yearly</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {/* Filter Icon */}
            <Tooltip title="Toggle Assignees">
              <IconButton onClick={handleFilterClick}>
                <FilterAltIcon sx={{ color: "#aaa" }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* User Filter Popover */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{ zIndex: 99000 }}
          >
            <Box sx={{ p: 2, width: 250 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                Toggle Assignees
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                {allUsers.map((user) => (
                  <FormControlLabel
                    key={user}
                    control={
                      <Checkbox
                        checked={!hiddenUsers.has(user)}
                        onChange={() => toggleUserVisibility(user)}
                        sx={{ color: "#70C930" }}
                      />
                    }
                    label={user}
                  />
                ))}
              </Box>
            </Box>
          </Popover>
        </Box>
      )}

      {!Array.isArray(productivityData) || productivityData.length < 1 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            fontSize: "16px",
            fontWeight: "bold",
            padding: "20px",
          }}
        >
          No data available
        </Box>
      ) : view === "yearly" ? (
        <Box sx={{ height: "calc(100% - 80px)", overflowY: "auto", pr: 1 }}>
          {productivityData.map((item) => {
            const hasStoryPoints = item.monthlyTrend.some((m) => m.storyPoints > 0);
            return (
              <Box
                key={item.assignee}
                sx={{
                  backgroundColor: theme.palette.background.subBox,
                  borderRadius: "8px",
                  mb: 2,
                  px: 2,
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1,
                    pointer: "cursor",
                  }}
                  onClick={() => userClicked(item.assignee)}
                >
                  <Avatar src={item.assigneeAvatar} alt={item.assignee} />
                  <Typography sx={{ fontWeight: "bold" }}>{item.assignee}</Typography>
                </Box>
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={item.monthlyTrend}>
                    <defs>
                      <linearGradient id="colorPoints" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="10%" stopColor="#70C930" stopOpacity={0.5} />
                        <stop offset="90%" stopColor="#70C930" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="10%" stopColor="#F9CA00" stopOpacity={0.5} />
                        <stop offset="90%" stopColor="#F9CA00" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" stroke="#aaa" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#aaa" allowDecimals={false} tick={{ fontSize: 10 }} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: "#333", border: "none" }}
                      labelStyle={{ color: "#ccc" }}
                    />

                    {hasStoryPoints && (
                      <Area
                        type="monotone"
                        dataKey="storyPoints"
                        stroke="#70C930"
                        fill="url(#colorPoints)"
                        strokeWidth={2}
                        name="Story Points"
                      />
                    )}
                    <Area
                      type="monotone"
                      dataKey="issuesResolved"
                      stroke="#F9CA00"
                      fill="url(#colorTasks)"
                      strokeWidth={2}
                      name="Tasks Completed"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ height: "calc(100% - 80px)", overflowY: "auto", pr: 1 }}>
          {productivityData.map((item) => (
            <Box
              key={item.assignee}
              sx={{
                backgroundColor: theme.palette.background.subBox,
                borderRadius: "8px",
                mb: 2,
                px: 2,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box
                onClick={() => userClicked(item.assignee)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  minWidth: 160,
                  cursor: "pointer",
                }}
              >
                <Avatar src={item.assigneeAvatar} alt={item.assignee} />
                <Box>
                  <Tooltip title={item.assignee}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "160px",
                      }}
                    >
                      {item.assignee}
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontSize: "12px", color: "#aaa" }}>
                    {item.issuesResolved} tickets | {item.totalStoryPoints} pts |{" "}
                    {item.averageCycleTime}d cycle
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flexGrow: 1, minWidth: 150 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  {(() => {
                    const score = item.percentOfTeamAverage;
                    const isExceptional = score >= 300;
                    const isTop = score >= 200;
                    const isAbove = score >= 100;

                    const bgColor = isExceptional
                      ? "#70C93033"
                      : isTop
                        ? "#70C93033"
                        : isAbove
                          ? "#F9CA0033"
                          : "#444"; // neutral tone

                    const textColor = isExceptional
                      ? "#70C930"
                      : isTop
                        ? "#70C930"
                        : isAbove
                          ? "#F9CA00"
                          : "#aaa";

                    const label = score >= 300 ? ">300% of avg" : `${score}% of avg`;

                    return (
                      <>
                        <Tooltip title="Based on tickets, speed, and effort">
                          <Box
                            sx={{
                              px: 1.2,
                              py: 0.3,
                              fontSize: "12px",
                              fontWeight: "bold",
                              borderRadius: "999px",
                              backgroundColor: bgColor,
                              color: textColor,
                            }}
                          >
                            {label}
                          </Box>
                        </Tooltip>

                        {isExceptional ? (
                          <Tooltip title="Very high contribution (relative to peers)">
                            <Box
                              sx={{
                                fontSize: 18,
                                lineHeight: 1,
                                color: "#70C930",
                              }}
                            >
                              🔥
                            </Box>
                          </Tooltip>
                        ) : isAbove ? (
                          <Tooltip title="High volume relative to team avg">
                            <Box
                              sx={{
                                fontSize: 18,
                                lineHeight: 1,
                                color: "#F9CA00",
                              }}
                            >
                              🏆
                            </Box>
                          </Tooltip>
                        ) : null}
                      </>
                    );
                  })()}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </WidgetContainer>
  );
};

export default React.memo(JiraProductivity);
